import { useState, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { getParameterLabel } from "@/lib/template-parameters"

interface ParameterBadgeProps {
  paramKey: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}

/**
 * 인라인 파라미터 뱃지 컴포넌트
 *
 * 템플릿 내 파라미터를 시각적으로 강조하고,
 * 클릭 시 Popover로 즉시 편집 가능.
 */
export function ParameterBadge({
  paramKey,
  value,
  onChange,
  disabled = false,
}: ParameterBadgeProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const label = getParameterLabel(paramKey)

  // Popover 열릴 때 Input에 포커스
  useEffect(() => {
    if (open && inputRef.current) {
      // 약간의 딜레이 후 포커스 (애니메이션 완료 대기)
      const timer = setTimeout(() => {
        inputRef.current?.focus()
        inputRef.current?.select()
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [open])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setOpen(false)
    }
  }

  return (
    <Popover open={disabled ? false : open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`inline px-2 py-0.5 mx-0.5 text-sm font-medium text-primary-foreground bg-primary rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 text-left ${
            disabled ? 'opacity-70 cursor-default' : 'cursor-pointer hover:bg-primary/90'
          }`}
          title={disabled ? undefined : t('generator.editParameter', { label })}
        >
          {value || label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="space-y-2">
          <Label
            htmlFor={`param-edit-${paramKey}`}
            className="text-xs font-medium text-slate-600"
          >
            {label}
          </Label>
          <Input
            ref={inputRef}
            id={`param-edit-${paramKey}`}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={label}
            className="h-9 text-sm"
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
